import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Checkbox, Col, DatePicker, Form, Input, Row
} from 'antd';
import ExtendedSelect from 'components/extendedSelect';
import moment from 'moment';
import _ from 'lodash';
import { parseDuration } from 'library/time';
import settings from 'settings';
import REDUCER_KEY from 'reducers/reducer.keys';
import { list } from 'actions';
import {
  convertToSelect, getKey, getLabel, setLowerDateRange, setUpperDateRange
} from 'library/helpers';
import { FormWrapper } from 'components/Page';

const FormItem = Form.Item;

const mapStateToProps = state => ({
  competencies: state.api.competencies.data.results,
  inFlight: state.api.competencies.inFlight
});

const mapDispatchToProps = dispatch => ({
  loadCompetencies(searchText) {
    // ------ Specify search params ------
    const sorted = [{
      id: 'name',
      desc: false
    }];
    const filters = [
      {
        id: 'active',
        value: true
      },
      {
        id: 'is_integration_managed',
        value: false
      }
    ];
    if (!_.isEmpty(searchText)) {
      filters.push({
        id: 'name__icontains',
        value: searchText
      });
    }
    dispatch(list(REDUCER_KEY.competencies.key, {
      page: 0,
      pageSize: settings.APP_CONFIG.dropListSize,
      filters,
      sorted
    }));
  }
});

const mapPropsToFields = (props) => {
  const { dataSource } = props;
  const {
    auto_expire, competency, assessor, scheduled, awarded_date, expiry_date
  } = dataSource;
  return {
    competency: Form.createFormField({ value: convertToSelect(competency) }),
    assessor: Form.createFormField({ value: assessor }),
    scheduled: Form.createFormField({ value: scheduled }),
    awarded_date: Form.createFormField({ value: awarded_date }),
    expiry_date: Form.createFormField({ value: expiry_date }),
    auto_expire: Form.createFormField({ value: auto_expire || false })
  };
};

const onFieldsChange = (props, fields) => {
  const { onFieldsChange: dispatchOnFieldsChange } = props;
  if (dispatchOnFieldsChange) {
    dispatchOnFieldsChange(fields);
  }
};

const onValuesChange = (props, values) => {
  const { onValuesChange: dispatchOnValuesChange } = props;
  if (dispatchOnValuesChange) {
    dispatchOnValuesChange(values);
  }
};

const FormInput = ({
  canAdmin, dataSource, checkboxFormItemLayout, inputFormItemLayout, form, inFlight, competencies, loadCompetencies
}) => {
  const { getFieldDecorator } = form;
  const {
    id, competency, assessor, scheduled, awarded_date, expiry_date, auto_expire
  } = dataSource;
  // load competencies on first load
  useEffect(() => {
    loadCompetencies();
  }, []);
  /*
   ** @description If Competency has been select and AutoExpire=true and validityPeriod > 0,
   ** set ExpiryDate = AwardedDate + Competency.ValidationPeriod
   ** @param competency Object
   ** @param autoExpire Boolean
   ** @param aradedDate Object
    */
  const setExpiryDate = (currentCompetency, autoExpire, awardedDate) => {
    if (currentCompetency && autoExpire) {
      const validityPeriod = parseDuration(competencies.filter(comp => comp.id === currentCompetency.key)[0].validity_period);
      if (awardedDate && validityPeriod) {
        form.setFieldsValue({
          expiry_date: moment(awardedDate)
            .add(validityPeriod.days, 'days')
        });
      }
    }
    else {
      form.setFieldsValue({ expiry_date: null });
    }
  };
  /*
   ** @description Update expiry_date when competency is changed
   ** @param value Object
    */
  const handleCompetencyChange = (value) => {
    const awardedDate = form.getFieldValue('awarded_date');
    const autoExpire = form.getFieldValue('auto_expire');
    setExpiryDate(value, autoExpire, awardedDate);
  };
  /*
   ** @description Update expiry_date when auto_expire is changed
   ** @param value Object
    */
  const handleAutoExpireChange = (event) => {
    const awardedDate = form.getFieldValue('awarded_date');
    const competencyValue = form.getFieldValue('competency');
    setExpiryDate(competencyValue, event.target.checked, awardedDate);
  };
  /*
   ** @description Update expiry_date when awarded_date is changed
   ** @param value Object
    */
  const handleAwardedDateChange = (value) => {
    const competencyValue = form.getFieldValue('competency');
    const autoExpire = form.getFieldValue('auto_expire');
    // Set the Expiry Date based on whether AutoExpire=True
    setExpiryDate(competencyValue, autoExpire, value);
  };

  const isEditMode = id && id > 0; // edit flag for competency select
  const readonly = !canAdmin;

  return (
    <Form>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Competency">
            {getFieldDecorator('competency', {
              initialValue: competency,
              rules: [
                {
                  required: true,
                  message: 'Please select a Competency.'
                }
              ]
            })(
              <ExtendedSelect
                fetching={inFlight}
                placeholder="Select Competency"
                disabled={isEditMode || readonly}
                dataSource={competencies}
                optionKey={getKey}
                optionLabel={getLabel}
                onSearch={loadCompetencies}
                onChange={handleCompetencyChange}
                onFocus={loadCompetencies}
                dropdownMatchSelectWidth={false}
              />
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Scheduled">
            {getFieldDecorator('scheduled', {
              initialValue: scheduled
            })(<DatePicker format="DD-MM-YYYY" disabled={readonly} />)}
          </FormItem>
        </Col>
      </Row>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Assessor">
            {getFieldDecorator('assessor', {
              initialValue: assessor
            })(<Input disabled={readonly} />)}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Awarded Date">
            {getFieldDecorator('awarded_date', {
              initialValue: awarded_date,
              rules: [
                {
                  required: true,
                  message: 'Please select an Awarded Date.'
                }
              ]
            })(
              <DatePicker
                disabled={readonly}
                format="DD-MM-YYYY"
                disabledDate={startValue => setUpperDateRange(startValue, expiry_date)}
                onChange={handleAwardedDateChange}
              />
            )}
          </FormItem>
        </Col>
      </Row>
      <Row {...inputFormItemLayout.row}>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...checkboxFormItemLayout}>
            {getFieldDecorator('auto_expire', {
              initialValue: auto_expire || false,
              valuePropName: 'checked'
            })(
              <Checkbox disabled={readonly} onChange={handleAutoExpireChange}>
                Auto Expire
              </Checkbox>
            )}
          </FormItem>
        </Col>
        <Col {...inputFormItemLayout.col}>
          <FormItem {...inputFormItemLayout.inputLayout} label="Expiry Date">
            {getFieldDecorator('expiry_date', {
              initialValue: expiry_date
            })(
              <DatePicker
                disabled={readonly || auto_expire}
                format="DD-MM-YYYY"
                disabledDate={endValue => setLowerDateRange(endValue, awarded_date)}
              />
            )}
          </FormItem>
        </Col>
      </Row>
    </Form>
  );
};

FormInput.propTypes = {
  canAdmin: PropTypes.bool,
  inFlight: PropTypes.bool,
  checkboxFormItemLayout: PropTypes.object,
  competencies: PropTypes.array,
  dataSource: PropTypes.object,
  form: PropTypes.object,
  loadCompetencies: PropTypes.func,
  inputFormItemLayout: PropTypes.object
};

FormInput.defaultProps = {
  canAdmin: false,
  inFlight: false,
  checkboxFormItemLayout: {},
  competencies: [],
  dataSource: {},
  form: {},
  inputFormItemLayout: {},
  loadCompetencies() {
  }
};

const ConnectedFormInput = connect(
  mapStateToProps,
  mapDispatchToProps
)(FormInput);

const UserCompetencyInput = FormWrapper(Form.create({
  mapPropsToFields,
  onFieldsChange,
  onValuesChange
})(ConnectedFormInput));

export default UserCompetencyInput;
